'use client';

import { Skeleton, Flex, Row, Col, Typography, Carousel } from 'antd'
import Image from 'next/image'
import { isNull } from '../../../../lib/utils';
import { useState, useRef, Suspense, useCallback, useMemo } from 'react';
import { createClientService } from '@/lib/http/client';
import Link from 'next/link';

const { Paragraph, Text } = Typography;

const maxSlides = 7;

// 提取子组件
const CarouselNavigation = ({ showPrev, showNext, onPrev, onNext }) => (
    <>
        {showPrev && (
            <Image
                src="https://public-read-bkt-oss.oss-cn-hangzhou.aliyuncs.com/PC/yiwuTrade/shop/icon_lf_arrow.png"
                alt="Previous"
                width={53}
                height={53}
                className="navigation-btn prev"
                onClick={onPrev}
                aria-label="Previous slide"
            />
        )}
        {showNext && (
            <Image
                src="https://public-read-bkt-oss.oss-cn-hangzhou.aliyuncs.com/PC/yiwuTrade/shop/icon_rt_arrow.png"
                alt="Next"
                width={53}
                height={53}
                className="navigation-btn next"
                onClick={onNext}
                aria-label="Next slide"
            />
        )}
    </>
);

const ShopItem = ({ item, onShopClick, onProductClick }) => (
    <div className='store_item' onClick={() => onShopClick(item)}>
        <Flex align='center'>
            <Image src={item.headPicUrl + '?x-oss-process=image/resize,w_100'} width={50} height={50} alt={item.name} className='store_header_img' />
            <div className='store_name'>{item.name}</div>
        </Flex>
        <div className='store_main'>{item.mainSell}</div>
        <Row gutter={16}>
            {item.products.map((product, index) => (
                <Col key={index} span={6}>
                    <div className='product_item_img' onClick={(e) => onProductClick(e, product)}>
                        <Image
                            src={product.picUrl + '?x-oss-process=image/resize,w_200'}
                            alt={product.name}
                            fill
                            loading="lazy"
                            className='product_img'
                        />
                    </div>
                </Col>
            ))}
        </Row>
    </div>
);

export default function Cyd({ cydList, cydDetails, cydDetailsList }) {
    if (isNull(cydList) || cydList.length <= 0) {
        return null
    }

    if (isNull(cydDetails)) {
        return null;
    }

    const [cyd, setCyd] = useState(cydDetails);
    const [cydId, setCydId] = useState(cydDetails.industrialBeltId);
    const [shopList, setShopList] = useState(cydDetailsList);
    const [loading, setLoading] = useState(false);
    const carouselRef = useRef(null);

    const slidesToShow = useMemo(() =>
        cydList.length > maxSlides ? maxSlides : cydList.length,
        [cydList.length]
    );

    const [currentSlide, setCurrentSlide] = useState(0);

    const showPrev = useMemo(() => currentSlide > 0, [currentSlide]);
    const showNext = useMemo(() =>
        currentSlide < cydList.length - slidesToShow,
        [currentSlide, cydList.length, slidesToShow]
    );

    const handleItemChange = useCallback(async (industrialBeltId) => {
        if (industrialBeltId === cydId) return;

        setLoading(true);
        try {
            const [cydRes, shopRes] = await Promise.all([
                createClientService().get('mtop.shop.pc.cyd.bestShop', { data: { industrialBeltId } }),
                createClientService().get('mtop.search.shop.industry.search', { data: { industryUnique: industrialBeltId, pageNo: 1, pageSize: 4 } })
            ]);

            setCyd(cydRes);
            setShopList(shopRes?.shops || []);
            setCydId(industrialBeltId);
        } catch (error) {
            console.error('Failed to fetch data:', error);
        } finally {
            setLoading(false);
        }
    }, [cydId]);

    // 骨架屏组件
    const renderSkeleton = () => (
        <Flex className='cyd_bx' gap={16}>
            <Skeleton.Node active style={{ width: 380, height: 428, borderRadius: 8 }} />
            <div style={{ flex: 1 }}>
                <Row gutter={[16, 16]}>
                    {[...Array(4)].map((_, i) => (
                        <Col span={12} key={i}>
                            <Skeleton active style={{ minWidth: 300, height: 200, borderRadius: 8 }} />
                        </Col>
                    ))}
                </Row>
            </div>
        </Flex>
    );

    const _renderBackPic = useCallback((backPic) => {
        if (isNull(backPic)) return '';
        try {
            const obj = JSON.parse(backPic);
            return obj?.pc || '';
        } catch (e) {
            return '';
        }
    }, []);

    const handleToShop = useCallback((item) => {
        window.open(`/shop/${item.id}`)
    }, []);

    const handleToProduct = useCallback((e, product) => {
        e.stopPropagation();
        window.open(`/productdetail/${product.id}`)
    }, []);

    const handleViewDetails = useCallback(() => {
        window.open(`/industrialBelt?id=${cyd.industrialBeltId}`)
    }, [cyd.industrialBeltId]);

    const handlePrev = useCallback(() => {
        carouselRef.current?.prev();
        setCurrentSlide(prev => Math.max(0, prev - 1));
    }, []);

    const handleNext = useCallback(() => {
        carouselRef.current?.next();
        setCurrentSlide(prev => Math.min(cydList.length - slidesToShow, prev + 1));
    }, [cydList.length, slidesToShow]);

    return (
        <div className='cyd_warp_bg'>
            <div className='cyd_warp'>
                <div className='box_warp'>
                    <Flex className='cyd_title'>
                        Source manufacturers by region
                        <Link href="/industrialBelt" target='_blank' className='btn_view_all'>
                            View all
                            <Image
                                src="https://public-read-bkt-oss.oss-cn-hangzhou.aliyuncs.com/PC/yiwuTrade/shop/icon_view_all.png"
                                width={16}
                                height={16}
                                alt="More"
                            />
                        </Link>
                    </Flex>

                    <div className="carousel-wrapper">
                        <CarouselNavigation
                            showPrev={showPrev}
                            showNext={showNext}
                            onPrev={handlePrev}
                            onNext={handleNext}
                        />

                        <Carousel
                            ref={carouselRef}
                            dots={false}
                            infinite={false}
                            speed={500}
                            slidesToShow={slidesToShow}
                            slidesToScroll={1}
                            centerPadding={"60px"}
                            className="custom-carousel"
                        >
                            {cydList.map((item, index) => (
                                <div className={`carousel-item-box`} key={index}>
                                    <div
                                        className={`${item.industrialBeltId == cydId ? 'active' : ''} carousel-item`}
                                        onClick={() => handleItemChange(item.industrialBeltId)}
                                    >
                                        <Text ellipsis={{ tooltip: item.industrialBeltName }} className='cyd_name'>
                                            {item.industrialBeltName}
                                        </Text>
                                        <div>{item.address}</div>
                                    </div>
                                </div>
                            ))}
                        </Carousel>
                    </div>

                    <Suspense>
                        <Flex align='center' className='cyd_bx'>
                            {loading ? renderSkeleton() : (
                                <>
                                    <Flex
                                        align='center'
                                        justify='center'
                                        className='lf_desc'
                                        style={{ backgroundImage: `url(${_renderBackPic(cyd.backPic)})` }}
                                    >
                                        <div className='desc_bx'>
                                            <h5>{cyd.industrialBeltName}</h5>
                                            <Paragraph ellipsis={{ rows: 9 }} className='desc_introduction'>
                                                {cyd.introduction}
                                            </Paragraph>
                                            <Flex align='center' justify='center' className='btn_details' onClick={handleViewDetails}>
                                                View details
                                            </Flex>
                                        </div>
                                    </Flex>
                                    <div className='rt_bx'>
                                        <Row gutter={[16, 16]}>
                                            {shopList?.map((item, index) => (
                                                <Col span={12} key={index}>
                                                    <ShopItem
                                                        item={item}
                                                        onShopClick={handleToShop}
                                                        onProductClick={handleToProduct}
                                                    />
                                                </Col>
                                            ))}
                                        </Row>
                                    </div>
                                </>
                            )}
                        </Flex>
                    </Suspense>
                </div>
            </div>
        </div>
    );
}